import style from "./tooltip.module.css";
import { useRef, useState } from "react";
import {
  useFloating,
  autoUpdate,
  offset,
  flip,
  shift,
  useHover,
  useFocus,
  useDismiss,
  useRole,
  useInteractions,
  arrow,
  FloatingArrow,
} from "@floating-ui/react";

interface Props {
  showArrow?: boolean;
  trigger: React.ReactNode;
  description: string;

  highContrast?: boolean;
  sidebar?: boolean;
  fixed?: boolean;
}
function Tooltip({ showArrow, trigger, description, highContrast, fixed, sidebar=false}: Readonly<Props>) {
  const [isOpen, setIsOpen] = useState(false);
  const arrowRef = useRef(null);

  const { refs, floatingStyles, context } = useFloating({
    placement: sidebar ? "left" : "bottom",
    open: isOpen,
    onOpenChange: setIsOpen,
    middleware: [offset({ mainAxis: 7 }), flip(), shift(), arrow({ element: arrowRef })],
    whileElementsMounted: autoUpdate,
  });

  const hover = useHover(context, { move: false, delay: { open: 600 } }); // Toggle tooltip on or off on hover
  const focus = useFocus(context); // Toggle tooltip on or off on focus
  const dismiss = useDismiss(context); // Dismiss tooltip when 'esc' is pressed
  const role = useRole(context, {
    // Correct ARIA attributes for screen readers
    role: "tooltip",
  });

  // Merge all interactions into prop getters
  const { getReferenceProps, getFloatingProps } = useInteractions([hover, focus, dismiss, role]);

  const contrast = highContrast ? style.contrastWrapper : style.normalWrapper;
  const sidebarStyle = sidebar ? style.sidebarTooltip : "";
  const positionType = fixed ? style.fixedPosition : "";

  return (
    <div>
      <div ref={refs.setReference} {...getReferenceProps()}>
        {trigger}
      </div>
      {isOpen && (
        <div
          ref={refs.setFloating}
          {...getFloatingProps()}
          style={floatingStyles}
          className={style.wrapper + " " + contrast + " " + positionType + " " + sidebarStyle}
        >
          {showArrow && <FloatingArrow fill="var(--onedark-comment)" ref={arrowRef} context={context} />}
          <p className={highContrast ? style.text + " " + style.contrastText : style.text + " " + style.normalText}>
            {description}
          </p>
        </div>
      )}
    </div>
  );
}

export default Tooltip;
